import { Observable } from 'rxjs';
import { APIResponseWrapper } from '../../models/common/api-response-wrapper';
import * as HrModels from '../hr.models';
import { Constructor, HrApiClient } from '../shared/hr-api-client';

export function withHrRosterApi<TBase extends Constructor<HrApiClient>>(Base: TBase) {
  return class extends Base {
    rosterDashboard(owningCompanyId?: number): Observable<APIResponseWrapper<HrModels.HrRosterDashboard>> {
        return this.http.get<APIResponseWrapper<HrModels.HrRosterDashboard>>(`${this.baseUrl}/roster/dashboard`, {
          params: this.params({ owningCompanyId })
        });
      }
    
    shiftTemplates(owningCompanyId?: number): Observable<APIResponseWrapper<HrModels.HrShiftTemplate[]>> {
        return this.http.get<APIResponseWrapper<HrModels.HrShiftTemplate[]>>(`${this.baseUrl}/roster/shift-templates`, {
          params: this.params({ owningCompanyId })
        });
      }
    
    saveShiftTemplate(request: HrModels.HrShiftTemplate): Observable<APIResponseWrapper<HrModels.HrShiftTemplate>> {
        return request.shiftTemplateId
          ? this.http.put<APIResponseWrapper<HrModels.HrShiftTemplate>>(`${this.baseUrl}/roster/shift-templates/${request.shiftTemplateId}`, request)
          : this.http.post<APIResponseWrapper<HrModels.HrShiftTemplate>>(`${this.baseUrl}/roster/shift-templates`, request);
      }
    
    employeeWorkPatterns(owningCompanyId?: number, employeeId?: number, active?: boolean): Observable<APIResponseWrapper<HrModels.HrEmployeeWorkPattern[]>> {
        return this.http.get<APIResponseWrapper<HrModels.HrEmployeeWorkPattern[]>>(`${this.baseUrl}/roster/work-patterns`, {
          params: this.params({ owningCompanyId, employeeId, active })
        });
      }
    
    saveEmployeeWorkPattern(request: HrModels.HrEmployeeWorkPattern): Observable<APIResponseWrapper<HrModels.HrEmployeeWorkPattern>> {
        return request.workPatternId
          ? this.http.put<APIResponseWrapper<HrModels.HrEmployeeWorkPattern>>(`${this.baseUrl}/roster/work-patterns/${request.workPatternId}`, request)
          : this.http.post<APIResponseWrapper<HrModels.HrEmployeeWorkPattern>>(`${this.baseUrl}/roster/work-patterns`, request);
      }
    
    rosterPlans(owningCompanyId?: number, status?: string, from?: string, to?: string, limit?: number): Observable<APIResponseWrapper<HrModels.HrRosterPlan[]>> {
        return this.http.get<APIResponseWrapper<HrModels.HrRosterPlan[]>>(`${this.baseUrl}/roster/plans`, {
          params: this.params({ owningCompanyId, status, from, to, limit })
        });
      }
    
    saveRosterPlan(request: HrModels.HrRosterPlan): Observable<APIResponseWrapper<HrModels.HrRosterPlan>> {
        return request.rosterPlanId
          ? this.http.put<APIResponseWrapper<HrModels.HrRosterPlan>>(`${this.baseUrl}/roster/plans/${request.rosterPlanId}`, request)
          : this.http.post<APIResponseWrapper<HrModels.HrRosterPlan>>(`${this.baseUrl}/roster/plans`, request);
      }
    
    coverageRequirements(owningCompanyId?: number, rosterPlanId?: number, from?: string, to?: string): Observable<APIResponseWrapper<HrModels.HrCoverageRequirement[]>> {
        return this.http.get<APIResponseWrapper<HrModels.HrCoverageRequirement[]>>(`${this.baseUrl}/roster/coverage-requirements`, {
          params: this.params({ owningCompanyId, rosterPlanId, from, to })
        });
      }
    
    saveCoverageRequirement(request: HrModels.HrCoverageRequirement): Observable<APIResponseWrapper<HrModels.HrCoverageRequirement>> {
        return request.coverageRequirementId
          ? this.http.put<APIResponseWrapper<HrModels.HrCoverageRequirement>>(`${this.baseUrl}/roster/coverage-requirements/${request.coverageRequirementId}`, request)
          : this.http.post<APIResponseWrapper<HrModels.HrCoverageRequirement>>(`${this.baseUrl}/roster/coverage-requirements`, request);
      }
    
    rosterAssignments(rosterPlanId: number, owningCompanyId?: number): Observable<APIResponseWrapper<HrModels.HrRosterAssignment[]>> {
        return this.http.get<APIResponseWrapper<HrModels.HrRosterAssignment[]>>(`${this.baseUrl}/roster/plans/${rosterPlanId}/assignments`, {
          params: this.params({ owningCompanyId })
        });
      }
    
    saveRosterAssignment(rosterPlanId: number, request: HrModels.HrRosterAssignment): Observable<APIResponseWrapper<HrModels.HrRosterAssignment>> {
        return request.rosterAssignmentId
          ? this.http.put<APIResponseWrapper<HrModels.HrRosterAssignment>>(`${this.baseUrl}/roster/assignments/${request.rosterAssignmentId}`, request, {
              params: this.params({ rosterPlanId })
            })
          : this.http.post<APIResponseWrapper<HrModels.HrRosterAssignment>>(`${this.baseUrl}/roster/plans/${rosterPlanId}/assignments`, request);
      }
    
    deleteRosterAssignment(rosterAssignmentId: number, owningCompanyId?: number): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}/roster/assignments/${rosterAssignmentId}`, {
          params: this.params({ owningCompanyId })
        });
      }
    
    rosterConflicts(rosterPlanId: number, owningCompanyId?: number, limit?: number): Observable<APIResponseWrapper<HrModels.HrRosterConflict[]>> {
        return this.http.get<APIResponseWrapper<HrModels.HrRosterConflict[]>>(`${this.baseUrl}/roster/plans/${rosterPlanId}/conflicts`, {
          params: this.params({ owningCompanyId, limit })
        });
      }
    
    rosterChangeLog(rosterPlanId: number, owningCompanyId?: number, limit?: number): Observable<APIResponseWrapper<HrModels.HrRosterChangeLog[]>> {
        return this.http.get<APIResponseWrapper<HrModels.HrRosterChangeLog[]>>(`${this.baseUrl}/roster/plans/${rosterPlanId}/change-log`, {
          params: this.params({ owningCompanyId, limit })
        });
      }
    
    recalculateRosterConflicts(rosterPlanId: number, owningCompanyId?: number): Observable<APIResponseWrapper<HrModels.HrRosterConflict[]>> {
        return this.http.post<APIResponseWrapper<HrModels.HrRosterConflict[]>>(`${this.baseUrl}/roster/plans/${rosterPlanId}/recalculate-conflicts`, null, {
          params: this.params({ owningCompanyId })
        });
      }
    
    generateRosterFromWorkPatterns(rosterPlanId: number, request: HrModels.HrGenerateRosterFromPatternsRequest = {}): Observable<APIResponseWrapper<HrModels.HrRosterAssignment[]>> {
        return this.http.post<APIResponseWrapper<HrModels.HrRosterAssignment[]>>(`${this.baseUrl}/roster/plans/${rosterPlanId}/generate-from-work-patterns`, request);
      }
    
    publishRosterPlan(rosterPlanId: number, owningCompanyId?: number): Observable<APIResponseWrapper<HrModels.HrRosterPlan>> {
        return this.http.post<APIResponseWrapper<HrModels.HrRosterPlan>>(`${this.baseUrl}/roster/plans/${rosterPlanId}/publish`, null, {
          params: this.params({ owningCompanyId })
        });
      }
    
    unpublishRosterPlan(rosterPlanId: number, owningCompanyId?: number): Observable<APIResponseWrapper<HrModels.HrRosterPlan>> {
        return this.http.post<APIResponseWrapper<HrModels.HrRosterPlan>>(`${this.baseUrl}/roster/plans/${rosterPlanId}/unpublish`, null, {
          params: this.params({ owningCompanyId })
        });
      }
  };
}

